import React, { forwardRef, ReactElement, ReactNode, Ref, useImperativeHandle, useState } from "react";
import { Box, IconButton, makeStyles, TextField, TextFieldProps } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import Chip from "@material-ui/core/Chip";
import { tesaGrey3 } from "../../styles/colors";

export interface CustomMultipleTextFieldProps {
  disabled?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  maxLength?: number;
  placeholder?: string;
  placeholderIcon?: JSX.Element;
  onChange: (value: string[]) => void;
  resetStoredSearch?: () => void;
  value: string[];
}

const useInputStyles = makeStyles({
  root: {
    flexFlow: "wrap",
    fontSize: "0.875rem",
    "& input": {
      flexGrow: 1,
      width: "initial",
      height: "1.75rem",
      lineHeight: "1.75rem",
      padding: 0,
    },
    padding: "0.5rem 0.75rem",
  },
});

const useStyles = makeStyles({
  chip: {
    fontSize: "0.875rem",
    height: "1.5rem",
    marginBottom: "0.125rem",
    marginTop: "0.125rem",
    marginRight: "0.5rem",
    maxWidth: "30rem",
  },
  chipHighlighted: {
    opacity: 0.5,
  },
  chipIcon: {
    color: "currentColor",
    fontSize: "1em",
    height: "1em",
    marginRight: "0.5em",
    width: "1em",
  },
  placeHolderIcon: {
    alignItems: "center",
    color: tesaGrey3,
    display: "flex",
    height: "1.75rem",
    marginRight: "0.5em",
  },
  clearButton: {
    padding: "0.25rem",
  },
});

function CustomMultipleTextField(
  props: MergedProps<CustomMultipleTextFieldProps, TextFieldProps>,
  ref: Ref<{ reset: () => void; showUnenteredTerm: () => string }> | undefined,
): ReactElement {
  const classes = useStyles(props);
  const {
    disabled,
    clearable,
    fullWidth,
    maxLength,
    placeholder,
    placeholderIcon,
    onChange,
    resetStoredSearch,
    value,
    ...TextFieldProps
  } = props;

  const [term, setTerm] = useState("");
  const [lastSelected, setLastSelected] = useState(false);

  useImperativeHandle(ref, () => ({
    reset: (): void => {
      setTerm("");
    },
    showUnenteredTerm: (): string => term,
  }));

  const inputEmpty = value.length === 0 && !term.trim();

  const renderStart = (): ReactNode => {
    if (inputEmpty && placeholderIcon) {
      return <Box className={classes.placeHolderIcon}>{placeholderIcon}</Box>;
    }
    return value.map((item, index) => (
      <Chip
        // eslint-disable-next-line react/no-array-index-key
        key={item + index}
        className={
          index === value.length - 1 && lastSelected ? [classes.chip, classes.chipHighlighted].join(" ") : classes.chip
        }
        color="primary"
        tabIndex={-1}
        label={item}
        deleteIcon={<ClearIcon className={classes.chipIcon} />}
        onDelete={() => {
          setLastSelected(false);
          const newValue = [...value];
          newValue.splice(index, 1);
          onChange(newValue);
        }}
      />
    ));
  };

  const renderEnd = (): ReactNode => {
    if (!inputEmpty && clearable) {
      return (
        <IconButton
          className={classes.clearButton}
          onClick={() => {
            onChange([]);
            setTerm("");
            setLastSelected(false);
            if (resetStoredSearch) {
              resetStoredSearch();
            }
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      );
    }
  };

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        value={term}
        disabled={disabled}
        placeholder={inputEmpty ? placeholder : undefined}
        onBlur={() => {
          setLastSelected(false);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          setLastSelected(false);
          const target = event.target as HTMLInputElement;
          const selection = target.selectionStart !== target.selectionEnd;
          const newEntry = term.trim();
          if (event.key === "Enter" && newEntry) {
            event.preventDefault();
            onChange([...value, newEntry]);
            setTerm("");
          }
          if (event.key === "Backspace" && target.selectionStart === 0 && !selection) {
            event.preventDefault();
            if (lastSelected) {
              const newValue = [...value];
              newValue.pop();
              onChange(newValue);
            } else {
              setLastSelected(true);
            }
          }
        }}
        onChange={(event) => setTerm(event.target.value)}
        InputProps={{
          classes: useInputStyles(),
          inputProps: {
            maxLength: maxLength || 50,
          },
          startAdornment: renderStart(),
          endAdornment: renderEnd(),
        }}
        {...TextFieldProps}
      />
    </>
  );
}

export default forwardRef(CustomMultipleTextField);
